import { css } from 'styled-components';

import { solidButtonVariant, textButtonVariant } from './BaseStyledButtonVariants';
import { aafDarkButtons, ColorScheme } from './BaseStyledColorScheme';
import { BaseStyledElement } from './BaseStyledElements';
import { BaseStyledStyling } from './BaseStyledStyling';
import { media } from '~/components/shared/core/styles/media';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

/* ColorScheme lives in its own file; re-exported so existing imports keep working */
export { ColorScheme };

/* ArrowRightSendIcon path (16x16) */
const arrowIcon = `"data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><path d="m13.854 8.854-4.5 4.5a.5.5 0 1 1-.708-.707L12.293 9H2.5a.5.5 0 1 1 0-1h9.793L8.646 4.354a.5.5 0 1 1 .708-.707l4.5 4.5a.502.502 0 0 1 0 .707Z"/></svg>'
)}"`;

/**
 *  Base styling for Category Tags Scrolling
 *
 * ```css
 * --button-gap: ${theme.spacing.x['2xs']};
 * --categoryTagScrolling-grow-before: 0;
 * --categoryTagScrolling-grow-after: 1;
 * --categoryTagScrolling-inline-padding: ${theme.spacing.x.xs};
 * --categoryTagScrolling-gradient-padding: ${theme.spacing.x.xs};
 * ```
 *
 */
export const CategoryTagScrolling = css`
  ${({ theme }) => css`
    --inline-padding: var(--categoryTagScrolling-inline-padding, ${theme.spacing.x.xs});
    --gradient-padding: var(--categoryTagScrolling-gradient-padding, ${theme.spacing.x.xs});
    --grow-before: var(--categoryTagScrolling-grow-before, 0);
    --grow-after: var(--categoryTagScrolling-grow-after, 1);

    justify-content: start;
    flex-wrap: nowrap;
    padding-inline: var(--inline-padding);
    padding-block-end: 1px;
    overflow-y: hidden;
    overflow-x: auto;
    overflow-scrolling: touch; /* stylelint-disable-line property-no-unknown */
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    overscroll-behavior-inline: contain;

    &::-webkit-scrollbar {
      -webkit-appearance: none; /* stylelint-disable-line value-no-vendor-prefix, property-no-vendor-prefix */
      display: none;
    }

    mask-image: linear-gradient(
      to right,
      #f000 0,
      #f00 var(--gradient-padding, ${theme.spacing.x.xs}),
      #f00 calc(100% - var(--gradient-padding, ${theme.spacing.x.xs})),
      #f000 100%
    );

    &::before,
    &::after {
      content: '';
      scroll-snap-align: none;
      flex-grow: var(--grow-before);
      margin-inline: 0 calc(var(--button-gap, ${theme.spacing.x['2xs']}) * -1);
    }

    &::after {
      flex-grow: var(--grow-after);
      margin-inline: calc(var(--button-gap, ${theme.spacing.x['2xs']}) * -1) 0;
    }

    > * {
      flex: none;
      scroll-snap-align: start;
      scroll-margin-inline: var(--inline-padding, ${theme.spacing.x.xs});

      &:last-child {
        scroll-snap-align: end;
      }
    }
  `}
`;

export const BaseStyledModifier = {
  /**
   *  Base styling for Category Tags
   *
   * ```css
   * --contentBlock-button-gap: ${theme.spacing.x['2xs']};
   * ```
   *
   */
  CategoryTags: css`
    ${({ theme }) => css`
      --contentBlock-button-gap: ${theme.spacing.x['2xs']};

      :where(.contentBlock-body-links) {
        display: flex;
        flex-wrap: wrap;
      }

      :where(.contentBlock-body-link) {
        ${BaseStyledElement.categoryTag}
      }

      @media ${({ theme }) => media(theme).lessThan('sm')} {
        --categoryTagScrolling-inline-padding: 8px;
        --categoryTagScrolling-gradient-padding: 8px;

        :where(.contentBlock-body) {
          overflow: hidden;
        }

        :where(.contentBlock-bodyAlign) {
          width: 100%;
        }

        :where(.contentBlock-body-links) {
          width: calc(100% + var(--categoryTagScrolling-inline-padding) * 2);
          margin-inline-start: calc(var(--categoryTagScrolling-inline-padding) * -1);

          ${CategoryTagScrolling}
        }
      }
    `}
  `,
  CategoryTagScrolling,
  Layout: css`
    /* ALIGN HORIZONTAL/VERTICAL CLASSES */
    @media ${({ theme }) => media(theme).lessThan('sm')} {
      :has([class*='smTextAlignLeft']) {
        --contentBlock-text-align: left;
        --contentBlock-justify-content: start;
        --categoryTagScrolling-grow-before: 0;
        --categoryTagScrolling-grow-after: 1;
      }

      :has([class*='smTextAlignRight']) {
        --contentBlock-text-align: right;
        --contentBlock-justify-content: end;
        --categoryTagScrolling-grow-before: 1;
        --categoryTagScrolling-grow-after: 0;
      }

      &[class*='xsBodyVerticalAlignTop'] {
        --contentBlock-align-items: start;
      }

      &[class*='xsBodyVerticalAlignBottom'] {
        --contentBlock-align-items: end;
      }
    }

    @media ${({ theme }) => media(theme).between('sm', 'lg')} {
      &[class*='smBodyTextAlignLeft'] {
        --contentBlock-text-align: left;
        --contentBlock-justify-content: start;
      }

      &[class*='smBodyTextAlignRight'] {
        --contentBlock-text-align: right;
        --contentBlock-justify-content: end;
      }

      &[class*='smBodyVerticalAlignTop'] {
        --contentBlock-align-items: start;
      }

      &[class*='smBodyVerticalAlignBottom'] {
        --contentBlock-align-items: end;
      }
    }

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      &[class*='mdBodyTextAlignLeft'] {
        --contentBlock-text-align: left;
        --contentBlock-justify-content: start;
      }

      &[class*='mdBodyTextAlignRight'] {
        --contentBlock-text-align: right;
        --contentBlock-justify-content: end;
      }

      &[class*='mdBodyVerticalAlignTop'] {
        --contentBlock-align-items: start;
      }

      &[class*='mdBodyVerticalAlignBottom'] {
        --contentBlock-align-items: end;
      }
    }

    /* ALIGN HORIZONTAL/VERTICAL ATTRIBUTES */
    &[data-horizontal-align^='center'] {
      --contentBlock-text-align: center;
      --contentBlock-justify-content: center;
      --categoryTagScrolling-grow-before: 1;
      --categoryTagScrolling-grow-after: 1;
    }

    &[data-horizontal-align^='left'] {
      --contentBlock-text-align: left;
      --contentBlock-justify-content: start;
      --categoryTagScrolling-grow-before: 0;
      --categoryTagScrolling-grow-after: 1;
    }

    &[data-horizontal-align^='right'] {
      --contentBlock-text-align: right;
      --contentBlock-justify-content: end;
      --categoryTagScrolling-grow-before: 1;
      --categoryTagScrolling-grow-after: 0;
    }

    &[data-vertical-align^='top'] {
      --contentBlock-align-items: start;
    }

    &[data-vertical-align^='center'],
    &[data-vertical-align^='middle'] {
      --contentBlock-align-items: center;
    }

    &[data-vertical-align^='bottom'] {
      --contentBlock-align-items: end;
    }

    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      &[data-horizontal-align$=',center'] {
        --contentBlock-text-align: center;
        --contentBlock-justify-content: center;
      }

      &[data-horizontal-align$=',left'] {
        --contentBlock-text-align: left;
        --contentBlock-justify-content: start;
      }

      &[data-horizontal-align$=',right'] {
        --contentBlock-text-align: right;
        --contentBlock-justify-content: end;
      }

      &[data-vertical-align$=',center'],
      &[data-vertical-align$=',middle'] {
        --contentBlock-align-items: center;
      }

      &[data-vertical-align$=',top'] {
        --contentBlock-align-items: start;
      }

      &[data-vertical-align$=',bottom'] {
        --contentBlock-align-items: end;
      }
    }

    @media ${({ theme }) => media(theme).between('sm', 'lg')} {
      &[data-horizontal-align*=',center,'] {
        --contentBlock-text-align: center;
        --contentBlock-justify-content: center;
      }

      &[data-horizontal-align*=',left,'] {
        --contentBlock-text-align: left;
        --contentBlock-justify-content: start;
      }

      &[data-horizontal-align*=',right,'] {
        --contentBlock-text-align: right;
        --contentBlock-justify-content: end;
      }

      &[data-vertical-align*=',center,'],
      &[data-vertical-align*=',middle,'] {
        --contentBlock-align-items: center;
      }

      &[data-vertical-align*=',top,'] {
        --contentBlock-align-items: start;
      }

      &[data-vertical-align*=',bottom,'] {
        --contentBlock-align-items: end;
      }
    }
  `,

  ColorSchemes: css`
    ${aafDarkButtons}

    &:where(.contentBlock--dark) {
      ${ColorScheme.dark}
    }

    &:where(.contentBlock--light) {
      ${ColorScheme.light}
    }

    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      &:where(.contentBlock--smDark) {
        ${ColorScheme.initial}
      }

      &:where(.contentBlock--smLight) {
        ${ColorScheme.light}
      }
    }

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      &:where(.contentBlock--mdDark) {
        ${ColorScheme.initial}
      }

      &:where(.contentBlock--mdLight) {
        ${ColorScheme.light}
      }
    }
  `,
  /**
   * CMS button-type variants (Amplience "styling" field).
   *
   * Outline is the real default, applied unconditionally to every button.
   * contentBlock--buttonSolid / --buttonUnderline / --buttonLink are opt-in
   * classes that override it — solid re-fills the background outline
   * cleared, underline/link stay transparent and re-style the text. Each
   * also has a [data-button-type="solid|underline|link"] attribute
   * equivalent, for CMS setups that can't add a class at the same level.
   * [data-button-solid="1"] / "2" additionally force just the first/second
   * .contentBlock-body-link solid, independent of the overall button type.
   *
   * Variants re-route the --contentBlock-button-* variables, so they
   * compose with every ColorScheme (incl. the responsive sm/md classes).
   * New hover/active properties follow a naming rule: the default
   * (outline) keeps the plain --contentBlock-button-* namespace; every
   * opt-in variant gets its own qualifier (--contentBlock-buttonSolid-*,
   * --contentBlock-buttonText-* shared by underline/link).
   *
   * Must be applied on :where(.contentBlock) AFTER the
   * :where(.contentBlock-body-link) base block — everything is
   * specificity-neutral via :where(), so source order decides the cascade.
   */
  ButtonVariants: css`
    ${({ theme }) => css`
      /* OUTLINE is the real default — baked into contentBlockBodyLink + ColorScheme directly,
         nothing to override here for it */

      /* SOLID — class/data-attribute opt-in (all buttons), or a single-button
         override via [data-button-solid="1|2"], independent of the overall type */
      &:where(.contentBlock--buttonSolid, [data-button-type='solid'])
        :where(.contentBlock-body-link),
      &:where([data-button-solid='1']) :where(.contentBlock-body-link:first-child),
      &:where([data-button-solid='2']) :where(.contentBlock-body-link:nth-child(2)) {
        ${solidButtonVariant}
      }

      /* UNDERLINE */
      &:where(.contentBlock--buttonUnderline, [data-button-type='underline']) {
        --contentBlock-links-display: flex;
        --contentBlock-button-gap: ${theme.spacing.x.xs} ${theme.spacing.x.md};

        :where(.contentBlock-body-link) {
          ${textButtonVariant}
        }

        :where(.contentBlock-body-link-label) {
          text-decoration: underline;
          text-decoration-thickness: 2px;
          text-underline-offset: 5px;
          text-transform: uppercase;
        }
      }

      /* LINK (text + trailing arrow) */
      &:where(.contentBlock--buttonLink, [data-button-type='link']) {
        --contentBlock-links-display: flex;
        --contentBlock-button-gap: ${theme.spacing.x.xs} ${theme.spacing.x.md};

        :where(.contentBlock-body-link) {
          ${textButtonVariant}
          gap: 6px;

          @media (hover: hover) and (prefers-reduced-motion: no-preference) {
            &:hover,
            &:focus {
              :where(.contentBlock-body-link-icon) {
                transform: translateX(2px) rotate(45deg);
              }
            }
          }
        }

        :where(.contentBlock-body-link-label) {
          text-transform: uppercase;
        }

        :where(.contentBlock-body-link-icon) {
          /* markup renders icon before label; flex order moves it after */
          --size: 0.7em;
          --width: 2px;

          order: 1;
          flex: none;
          margin-top: -0.15em;
          border: 2px solid currentcolor;
          width: var(--size);
          height: var(--size);
          border-width: var(--width) var(--width) 0 0;
          transform: translateX(0) rotate(45deg);
          transform-origin: 50% 50%;
          transition: transform 0.3s ease-in-out;

          @media (prefers-reduced-motion) {
            transition: none;
          }
        }
      }

      /* per-siteType overrides per variant */
      ${({ theme }) =>
        getSiteStyles(theme.siteType, {
          [SiteType.Outlet]: css``,
          [SiteType.Aaf]: css`
            /* Link + icon button: trailing ArrowRightSendIcon as a mask (takes the text
               colour); no hover nudge in the AAF design system */
            &:where(
                .contentBlock--buttonIcon,
                .contentBlock--buttonLink,
                [data-button-type='link']
              ) {
              :where(.contentBlock-body-link) {
                gap: ${theme.spacing.x.xs};
              }

              :where(.contentBlock-body-link-icon),
              :where(.contentBlock-body-link):is(:hover, :focus)
                :where(.contentBlock-body-link-icon) {
                display: block;
                position: relative;
                flex: none;
                order: 1;
                width: 16px;
                height: 16px;
                margin: 0;
                border: none;
                transform: none;

                &::after {
                  content: '';
                  position: absolute;
                  inset: 0;
                  background-color: currentcolor;
                  mask: url(${arrowIcon}) center / contain no-repeat;
                }
              }
            }

            /* Link + underline: line across the whole button (1px, 2px on hover) instead
               of text-decoration; hover keeps the rest colour */
            &:where(
                .contentBlock--buttonLink,
                .contentBlock--buttonUnderline,
                [data-button-type='link'],
                [data-button-type='underline']
              ) {
              --contentBlock-buttonText-hover-color: var(
                --contentBlock-buttonText-color,
                ${theme.colors.slate['900']}
              );

              :where(.contentBlock-body-link) {
                min-height: 32px;
                box-shadow: inset 0 calc(var(--underline-width, 1px) * -1) 0 currentcolor;

                @media (hover: hover) {
                  &:hover,
                  &:focus {
                    --underline-width: 2px;
                  }
                }
              }

              :where(.contentBlock-body-link-label) {
                text-decoration: none;
              }
            }
          `,
        })}
    `}
  `,
  TextShadow: css`
    :where(.contentBlock-body-title),
    :where(.contentBlock-body-subTitle) {
      text-shadow: 1px 1px 3px
        var(--contentBlock-shadow-color, rgb(0 0 0 / var(--contentBlock-shadow-opacity, 0)));
    }
  `,
  Promo: css`
    ${({ theme }) => css`
      :where(.lockup-container) {
        --promo-viewportWidth: calc(var(--promo-breakpoint, 768) + var(--sideNavUnitless, 0));
        --promo-viewportPercentage: calc(100 / var(--promo-viewportWidth));
        --promo-titleSize: 120;
        --promo-title-headSize: 30;
        --promo-title-footSize: 40;
        --promo-title-subSize: 30;

        font-family: var(--font-body);
        font-size: calc(var(--promo-titleSize) * var(--promo-viewportPercentage) * 1vw);
        line-height: 0.95;
        font-weight: ${theme.variables.font.weight.bold};
        letter-spacing: normal;

        @media ${({ theme }) => media(theme).between('sm', 'lg')} {
          --promo-breakpoint: 1024;
          --promo-titleSize: 80;
          --promo-title-headSize: 20;
          --promo-title-footSize: 27;
          --promo-title-subSize: 20;
        }

        @media ${({ theme }) => media(theme).greaterThan('sm')} {
          .mobile-font-size {
            font-size: 100% !important;
            margin: 0 !important;
            display: inline-block;
          }
        }

        @media ${({ theme }) => media(theme).greaterThan('lg')} {
          --promo-breakpoint: 1680;
          --promo-titleSize: 110;
        }

        .head {
          font-size: 37.5%;
          font-size: calc((100% / var(--promo-titleSize)) * var(--promo-title-headSize));
          text-transform: uppercase;
          font-weight: 500;

          + .head,
          + .sub {
            margin-top: 1em;
          }

          margin-bottom: 1em;

          + .head {
            margin-bottom: 0;
          }
        }

        .sub,
        .message {
          font-size: 37.5%;
          font-size: calc((100% / var(--promo-titleSize)) * var(--promo-title-subSize));
          text-transform: none;
          font-weight: 500;
          line-height: 1.1em;
        }

        .main {
          ${BaseStyledStyling.HeadingStyling}
          margin-left: -0.025em;

          > .mobile-font-size {
            margin-left: 0.025em;
          }

          + .foot {
            margin-top: 0.2em;
          }

          + .sub,
          + .message {
            margin-top: 0.9em;
          }
        }

        .foot {
          margin-top: auto;
          font-size: 50%;
          font-size: calc((100% / var(--promo-titleSize)) * var(--promo-title-footSize));
          font-weight: ${theme.variables.font.weight.bold};
          text-transform: uppercase;
          line-height: 0.9em;

          + .sub {
            margin-top: 0.9em;
          }
        }

        small,
        .small {
          display: block;
          margin-block-start: 0.9em;
          font-weight: ${theme.variables.font.weight.regular};
          font-size: 13px;
          line-height: 1.1em;
          text-transform: none;
        }
      }

      /* Layout / Countdown / APP */
      &:where(:has(.lockup-container)),
      &:where([data-countdown-date]),
      :where(:has(.download-container)) {
        .contentBlock-bodyAlign {
          display: grid;
          grid-template-rows: 1fr auto;
          height: 100%;
          width: 100%;
          justify-items: var(--contentBlock-justify-content, start);
          align-items: end;

          @media ${({ theme }) => media(theme).greaterThan('sm')} {
            grid-template-rows: 1fr auto;
            grid-template-columns: 1fr 1fr;
            height: 100%;
            width: 100%;

            .contentBlock-body-title {
              grid-row: 1 / span 1;
              grid-column: 1 / -1;
            }

            .contentBlock-body-subTitle {
              grid-row: 2 / span 1;
              grid-column: 2 / 2;
              align-self: end;
              justify-self: end;
            }

            .contentBlock-body-text {
              grid-column: 1 / -1;
            }

            .contentBlock-body-links {
              grid-row: 2 / span 1;
              grid-column: 1 / -1;
            }

            :has(.contentBlock-body-links) {
              .contentBlock-body-title {
                margin-top: 40px;
              }
            }
          }
        }

        .contentBlock-body-title {
          align-self: center;
        }
      }

      :where(:has(.download-container)) {
        .contentBlock-bodyAlign {
          @media ${({ theme }) => media(theme).greaterThan('sm')} {
            .contentBlock-body-subTitle {
              + .contentBlock-body-text {
                margin-bottom: 32px;
              }
            }

            .contentBlock-body-text {
              display: flex;
              justify-content: end;
              grid-row: 1 / 4;
              grid-column: 2 / -1;
              align-self: center;
              justify-self: end;
            }
          }
        }

        .download-container {
          display: flex;
          flex-direction: row;
          justify-content: start;
          align-items: stretch;

          .download-buttons {
            display: flex;
            gap: 8px;
          }

          @media ${({ theme }) => media(theme).lessThan('sm')} {
            .download-qr {
              display: none;
            }

            .download-buttons {
              margin-block-start: 24px;
            }
          }

          @media ${({ theme }) => media(theme).greaterThan('sm')} {
            flex-direction: column;
            width: clamp(150px, 35%, 256px);
            margin-inline-end: -2%;

            .download-buttons {
              flex-direction: column;
              padding: 0 5%;
            }

            img {
              width: 100%;
              height: auto;
            }
          }
        }

        .has-touch.is-ios & {
          .app-download--android {
            display: none;
          }
        }

        .has-touch:not(.is-ios) & {
          .app-download--ios {
            display: none;
          }
        }
      }
    `}
  `,
  Visible: css`
    :where(.is-mobile),
    :where(.is-tablet),
    :where(.is-desktop) {
      display: none;
    }

    @media ${({ theme }) => media(theme).lessThan('sm')} {
      :where(.is-mobile) {
        display: revert;
      }
    }

    @media ${({ theme }) => media(theme).between('sm', 'lg')} {
      :where(.is-tablet) {
        display: revert;
      }
    }

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      :where(.is-desktop) {
        display: revert;
      }
    }
  `,
  WrapButtons: css`
    &:where([data-wrap-buttons]) {
      --contentBlock-links-display: flex !important;
    }

    &:where([data-nowrap-buttons]) {
      --contentBlock-links-display: inline grid !important;
    }
  `,
};

export const {
  Layout,
  ColorSchemes,
  ButtonVariants,
  CategoryTags,
  TextShadow,
  Promo,
  Visible,
  WrapButtons,
} = BaseStyledModifier;
